import React from 'react';
import * as Headless from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/24/outline';

import {
  type CaraAttachment,
  type CaraTemplateAttachmentKind
} from '~/components/cara-input/cara-attachment';
import { AttachmentPickerMenu } from './cara-input-attachment-button';
import { CaraInputAttachments } from './cara-input-attachments';
import { TemplatePickerDialogs, type CaraInputTemplatePicker } from './cara-input';

/**
 * The output-template picker cluster shared by the preset editor and the
 * "Convert to Preset" wizard: the queued-attachment chips, a pill that opens
 * the add-template/form menu, and the three library picker dialogs.
 *
 * Owns the "which picker dialog is open" state internally — that is purely a
 * detail of this cluster, identical everywhere it appears. The caller supplies
 * the attachment queue (`attachments` + `onAdd` + `onRemove`, which map onto
 * either a reducer or a `useCaraAttachments` hook) and the per-kind picker
 * options; the pill copy differs per caller so it is a prop too.
 */
export const OutputTemplatePicker = (props: {
  attachments: CaraAttachment[];
  onAdd: (...next: CaraAttachment[]) => void;
  onRemove: (id: string) => void;
  /** Pill button text, e.g. "Add template or form". */
  buttonLabel: string;
  /** Accessible label for the pill button. */
  buttonAriaLabel: string;
  customFormPicker?: CaraInputTemplatePicker;
  wordTemplatePicker?: CaraInputTemplatePicker;
  excelTemplatePicker?: CaraInputTemplatePicker;
  htmlTemplatePicker?: CaraInputTemplatePicker;
}) => {
  const [openDialog, setOpenDialog] = React.useState<CaraTemplateAttachmentKind | null>(null);

  return (
    <>
      <div data-slot="control" className="flex flex-col gap-2">
        {props.attachments.length > 0 && (
          <CaraInputAttachments attachments={props.attachments} onRemove={props.onRemove} />
        )}
        <Headless.Menu as="div" className="relative inline-block self-start">
          <Headless.MenuButton
            as="button"
            type="button"
            aria-label={props.buttonAriaLabel}
            className="group text-neutral-600 dark:text-neutral-400 rounded-full border border-neutral-200 dark:border-neutral-700 flex flex-row items-center gap-1 text-xs py-1 px-1.5 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white data-[disabled]:opacity-50 data-[disabled]:hover:bg-white dark:data-[disabled]:hover:bg-white/10 transition-all duration-150"
          >
            <PlusIcon className="size-3 group-hover:text-neutral-900 dark:group-hover:text-white transition-transform duration-200 group-data-[open]:rotate-90" />
            {props.buttonLabel}
          </Headless.MenuButton>
          <AttachmentPickerMenu
            onPickCustomForm={() => setOpenDialog('custom-form')}
            onPickWordTemplate={() => setOpenDialog('word-template')}
            onPickExcelTemplate={
              props.excelTemplatePicker ? () => setOpenDialog('excel-template') : undefined
            }
            onPickHtmlTemplate={() => setOpenDialog('html-template')}
          />
        </Headless.Menu>
      </div>
      <TemplatePickerDialogs
        attachments={props.attachments}
        add={props.onAdd}
        remove={props.onRemove}
        openDialog={openDialog}
        onCloseDialog={() => setOpenDialog(null)}
        customFormPicker={props.customFormPicker}
        wordTemplatePicker={props.wordTemplatePicker}
        excelTemplatePicker={props.excelTemplatePicker}
        htmlTemplatePicker={props.htmlTemplatePicker}
      />
    </>
  );
};
